<template>
	<div class="view-ui-media-instagram">
		<div class="instagram-container">
			<div class="embed-container">
				<blockquote
					class="instagram-media"
					v-bind:data-instgrm-permalink="'https://www.instagram.com/p/' + postId"
				>
					<a v-bind:href="'https://www.instagram.com/p/' + postId" target="_blank" rel="noopener">
						View this post on Instagram
					</a>
				</blockquote>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * Instagram post embed component that creates responsive Instagram embeds
	 * from post URLs with automatic script loading and error handling.
	 *
	 * Usage Examples:
	 *
	 * Basic usage:
	 * <ViewInstagramEmbed post-url="https://www.instagram.com/p/CL4nT_bBBJa/" />
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Ui/Media
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewUiMediaInstagram extends ViewBase {
		/**
		 * Extracted post ID from the URL
		 *
		 * @return string | null
		 */
		public get postId(): string | null {
			if (!this.postUrl) {
				return null;
			}

			const postIdMatch = this.postUrl.match(/instagram\.com\/p\/([^\/\?]+)/);

			return postIdMatch ? postIdMatch[1] : null;
		}

		/**
		 * The Instagram post URL to embed
		 *
		 * @type string
		 */
		@Prop({ required: true })
		public postUrl!: string;

		/**
		 * Component mounted lifecycle
		 *
		 * @return void
		 */
		@mounted
		protected setup(): void {
			this.loadInstagramScript();
		}

		/**
		 * Loads the Instagram embed script if not already loaded
		 *
		 * @return Promise<void>
		 */
		protected async loadInstagramScript(): Promise<void> {
			// If already loaded, process embeds and return early
			if (document.getElementById('instagram-embed-script')) {
				if (window.instgrm?.Embeds?.process) {
					window.instgrm.Embeds.process();
				}
				return;
			}

			// Load the Instagram embed script
			await new Promise<void>((resolve, reject) => {
				const script = document.createElement('script');
				script.id = 'instagram-embed-script';
				script.async = true;
				script.src = 'https://www.instagram.com/embed.js';

				script.onload = () => resolve();
				script.onerror = () => reject(new Error('Failed to load Instagram embed script'));

				document.body.appendChild(script);
			});

			// After the script is loaded, process Instagram embeds
			if (window.instgrm?.Embeds?.process) {
				window.instgrm.Embeds.process();
			}
		}
	}
</script>

<style lang="scss">
	.view-ui-media-instagram {
		position: relative;

		.instagram-container {
			position: relative;
			width: 100%;

			.embed-container {
				width: 100%;

				// Instagram embed responsive styling
				.instagram-media {
					margin: 0 auto !important;
					max-width: 540px;
					min-width: 326px;
					width: 100%;
				}
			}
		}
	}
</style>
